import {
  House,
  FilePlus,
  FileText,
  ChartLineUp,
  ChatCircle,
  Megaphone,
  Bell,
  Gear,
  DownloadSimple,
} from '@phosphor-icons/react';
import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import { ADMIN_NAV_ITEMS } from '@/features/admin/utils/adminNavConfig';
import type { AdminNavItem } from '@/features/admin/utils/adminNavConfig';

export interface DockApp {
  id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: PhosphorIcon | React.ComponentType<any>;
  labelKey: string;
  to: string;
  color: string;
  /** Vibrant gradient for Liquid Glass icon background [top, bottom] */
  gradient?: [string, string];
  /** Tinted glow shadow color for the icon */
  glow?: string;
  perm?: 'canCreateReports' | `tab:${string}`;
  /** When set, fires onAction(action) instead of navigating */
  action?: string;
}

/**
 * General routes — available to all authenticated users or gated by canCreateReports.
 * These are the non-admin pages from App.tsx.
 */
const GENERAL_APPS: DockApp[] = [
  { id: 'home', icon: House, labelKey: 'nav.home', to: '/home', color: '#4ed3ff', gradient: ['#4ed3ff', '#0a84ff'], glow: 'rgba(10, 132, 255, 0.35)' },
  { id: 'addReport', icon: FilePlus, labelKey: 'home.actions.addReport', to: '/reports', color: '#4ed3ff', gradient: ['#4ed3ff', '#0a84ff'], glow: 'rgba(10, 132, 255, 0.35)', perm: 'canCreateReports', action: 'quickReport' },
  { id: 'reports', icon: FileText, labelKey: 'home.actions.newReport', to: '/reports', color: '#4ed3ff', gradient: ['#4ed3ff', '#0a84ff'], glow: 'rgba(10, 132, 255, 0.35)', perm: 'canCreateReports' },
  { id: 'dashboard', icon: ChartLineUp, labelKey: 'home.actions.dashboard', to: '/dashboard', color: '#4ed3ff', gradient: ['#4ed3ff', '#0a84ff'], glow: 'rgba(10, 132, 255, 0.35)', perm: 'canCreateReports' },
  { id: 'messages', icon: ChatCircle, labelKey: 'home.actions.messages', to: '/messages', color: '#4ed3ff', gradient: ['#4ed3ff', '#0a84ff'], glow: 'rgba(10, 132, 255, 0.35)' },
  { id: 'feed', icon: Megaphone, labelKey: 'home.actions.feed', to: '/feed', color: '#4ed3ff', gradient: ['#4ed3ff', '#0a84ff'], glow: 'rgba(10, 132, 255, 0.35)' },
  { id: 'notifications', icon: Bell, labelKey: 'home.actions.notifications', to: '/notifications', color: '#4ed3ff', gradient: ['#4ed3ff', '#0a84ff'], glow: 'rgba(10, 132, 255, 0.35)' },
  { id: 'settings', icon: Gear, labelKey: 'home.actions.settings', to: '/settings', color: '#4ed3ff', gradient: ['#4ed3ff', '#0a84ff'], glow: 'rgba(10, 132, 255, 0.35)' },
  { id: 'downloads', icon: DownloadSimple, labelKey: 'home.actions.downloads', to: '/downloads', color: '#4ed3ff', gradient: ['#4ed3ff', '#0a84ff'], glow: 'rgba(10, 132, 255, 0.35)' },
];

/** Color palette for admin tab dock icons */
const ADMIN_TAB_COLORS: Record<string, string> = {
  oversight: '#7c3aed',
  payroll: '#ea580c',
  employment: '#0891b2',
  resources: '#4f46e5',
  system: '#64748b',
};

/** Gradient + glow palette for admin groups */
const ADMIN_TAB_GRADIENTS: Record<string, { gradient: [string, string]; glow: string }> = {
  oversight: { gradient: ['#a78bfa', '#7c3aed'], glow: 'rgba(124, 58, 237, 0.35)' },
  payroll: { gradient: ['#fb923c', '#ea580c'], glow: 'rgba(234, 88, 12, 0.35)' },
  employment: { gradient: ['#22d3ee', '#0891b2'], glow: 'rgba(8, 145, 178, 0.35)' },
  resources: { gradient: ['#818cf8', '#4f46e5'], glow: 'rgba(79, 70, 229, 0.35)' },
  system: { gradient: ['#94a3b8', '#64748b'], glow: 'rgba(100, 116, 139, 0.30)' },
};

/**
 * Auto-generate dock apps from the admin nav config.
 * Each admin tab becomes a possible shortcut, gated by its `tab:*` permission.
 */
function buildAdminApps(): DockApp[] {
  return ADMIN_NAV_ITEMS.map((item: AdminNavItem) => {
    const gradientDef = ADMIN_TAB_GRADIENTS[item.group];
    return {
      id: `admin:${item.tabId}`,
      icon: item.icon,
      labelKey: item.labelKey,
      to: item.path,
      color: ADMIN_TAB_COLORS[item.group] ?? '#475569',
      gradient: gradientDef?.gradient,
      glow: gradientDef?.glow,
      perm: `tab:${item.tabId.replace(/-/g, '_')}` as `tab:${string}`,
    };
  });
}

/** Full registry — general pages + auto-discovered admin tabs */
export const DOCK_APPS: DockApp[] = [...GENERAL_APPS, ...buildAdminApps()];

/** Default shortcuts shown when user has no saved config */
export const DEFAULT_DOCK_IDS = [
  'home', 'addReport', 'reports', 'dashboard', 'messages',
  'admin:dashboard', 'admin:approvals', 'admin:activity', 'admin:users',
];

interface PermissionContext {
  canCreateReports: boolean;
  hasPermission: (perm: string) => boolean;
}

export function filterByPermissions(apps: DockApp[], perms: PermissionContext): DockApp[] {
  return apps.filter((app) => {
    if (app.perm === 'canCreateReports') return perms.canCreateReports;
    if (app.perm?.startsWith('tab:')) return perms.hasPermission(app.perm);
    return true; // no perm = available to all
  });
}
